import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { AuthShell } from "./AuthShell";
import { GoogleButton } from "./GoogleButton";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/PasswordInput";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { errMsg } from "@/lib/api";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

export default function Signup() {
  const { register, user } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ full_name: "", email: "", phone: "", password: "", confirm: "" });
  const [loading, setLoading] = useState(false);

  useEffect(() => { if (user) navigate("/dashboard", { replace: true }); }, [user, navigate]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (form.password !== form.confirm) { toast.error("Passwords do not match"); return; }
    if (form.password.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    setLoading(true);
    try {
      await register({ full_name: form.full_name, email: form.email, phone: form.phone, password: form.password });
      toast.success("Account created!");
      navigate("/dashboard");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell
      title="Create your account"
      subtitle="Start sending and delivering packages today."
      footer={<>Already have an account? <Link to="/login" className="text-primary font-semibold" data-testid="link-login">Log in</Link></>}
    >
      <form onSubmit={submit} className="space-y-4" data-testid="signup-form">
        <div className="space-y-1.5">
          <Label>Full Name</Label>
          <Input data-testid="signup-name-input" value={form.full_name} onChange={set("full_name")} placeholder="Jane Doe" className="rounded-xl h-11" required />
        </div>
        <div className="space-y-1.5">
          <Label>Email</Label>
          <Input data-testid="signup-email-input" type="email" value={form.email} onChange={set("email")} placeholder="you@example.com" className="rounded-xl h-11" required />
        </div>
        <div className="space-y-1.5">
          <Label>Phone Number</Label>
          <Input data-testid="signup-phone-input" value={form.phone} onChange={set("phone")} placeholder="+91 98765 43210" className="rounded-xl h-11" required />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label>Password</Label>
            <PasswordInput data-testid="signup-password-input" value={form.password} onChange={set("password")} placeholder="••••••••" className="rounded-xl h-11" required />
          </div>
          <div className="space-y-1.5">
            <Label>Confirm Password</Label>
            <PasswordInput data-testid="signup-confirm-input" value={form.confirm} onChange={set("confirm")} placeholder="••••••••" className="rounded-xl h-11" required />
          </div>
        </div>
        <Button type="submit" disabled={loading} data-testid="signup-submit-button" className="w-full rounded-xl h-11">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create account"}
        </Button>
      </form>
      <div className="mt-5"><GoogleButton label="Sign up with Google" /></div>
    </AuthShell>
  );
}
